import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';
import { generatePath } from 'react-router';
import { LandStatus, LandType, ZoneType } from '~/components/Land/Land.hooks';
import { UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { useNow } from '~/components/Util';

export type ParticipationStatus = 'NOT_PARTICIPATION' | 'PARTICIPATION' | 'WINNING' | 'NOT_WINNING';

export interface LandEventMyParticipation {
  zoneId: number;
  zoneType: ZoneType;
  // transformed data
  zoneNumber: number;
  participatedAt: Date;
  citizenNftCount: number;
  levelUpPassCount: number;
}

export interface LandEventZone {
  zoneId: number;
  zoneType: ZoneType;
  rewardAmount: number;
}

export interface LandEventLand {
  landId: number;
  landType: LandType;
  citizenNftLevel: number;
  winningRate: number; // 0.4
  rewardCurrency: string;
  rewardAmount: number;
  isParticipatable: boolean;
  // transformed data
  isParticipated: boolean;
  myParticipation?: LandEventMyParticipation;
  zoneList: LandEventZone[];
}

export type EssentialLandEvent = {
  week: number;
  startAt: Date;
  endAt: Date;
  status: LandStatus;
  myParticipationStatus: ParticipationStatus;
};

export type LandEvent = {
  announcedAt: Date;
  landList: LandEventLand[];
} & EssentialLandEvent;

export const landEventUrl = '/api/citizen/v1/lands/weekly/:id';

const transformLandEvent = (response: LandEvent) => ({
  ...response,
  startAt: new Date(response.startAt),
  endAt: new Date(response.endAt),
  announcedAt: new Date(response.announcedAt),
  landList: response.landList
    .map((each) => ({
      ...each,
      isParticipated: !!each.myParticipation,
      myParticipation: each.myParticipation
        ? {
            ...each.myParticipation,
            participatedAt: new Date(each.myParticipation.participatedAt),
            zoneNumber: Number(each.myParticipation.zoneType.split('_')[1]),
          }
        : undefined,
    }))
    .sort((a, b) => {
      const delta =
        Number(b.isParticipated || b.isParticipatable) -
        Number(a.isParticipated || a.isParticipatable);
      return delta !== 0 ? delta : b.citizenNftLevel - a.citizenNftLevel;
    }),
});
const requestLandEvent = async (id: number) => {
  const { data } = await axios.get<LandEvent>(generatePath(landEventUrl, { id }));
  return transformLandEvent(data);
};
export const useLandEvent = (id: number, options?: UseDosiminQueryOptions<LandEvent>) => {
  const client = useQueryClient();
  return useQuery<LandEvent>(['land', 'event', id], () => requestLandEvent(id), {
    initialData: (): LandEvent => {
      const data = client.getQueryData<InfiniteData<LandEventPageModel>>(['land', 'event']);
      const landEvent = data?.pages[0]?.weekList.find(({ week }) => week === id);
      return {
        week: id,
        startAt: new Date(),
        endAt: new Date(),
        announcedAt: new Date(),
        status: 'END',
        landList: [],
        myParticipationStatus: 'NOT_PARTICIPATION',
        ...landEvent,
      };
    },
    ...options,
  });
};

export const useSyncLandEventStatus = (id: number, options?: UseDosiminQueryOptions<LandEvent>) => {
  const now = useNow();
  const {
    data: { startAt, endAt },
  } = useLandEvent(id, options);
  const syncStatus = useMemo<LandStatus>(() => {
    if (now < startAt) {
      return 'UPCOMING';
    } else if (now >= startAt && now < endAt) {
      return 'IN_PROGRESS';
    } else {
      return 'END';
    }
  }, [endAt, now, startAt]);

  return syncStatus;
};

export interface LandEventPageModel {
  weekList: EssentialLandEvent[];
  page: {
    pageNo: number;
    totalElements: number;
    totalPageSize: number;
    isLast: boolean;
  };
}

export const landEventsUrl = '/api/citizen/v1/lands/weekly';
export const requestLandEvents = ({ pageNo = 0, pageSize = 20 }) =>
  axios
    .get<LandEventPageModel>(landEventsUrl, { params: { pageNo, pageSize } })
    .then(({ data }) => ({
      ...data,
      weekList: data.weekList.map((each) => ({
        ...each,
        startAt: new Date(each.startAt),
        endAt: new Date(each.endAt),
      })),
    }));

export type UseLandEventsOptions = UseInfiniteQueryOptions<LandEventPageModel>;
export const useLandEvents = (options?: UseLandEventsOptions) =>
  useInfiniteQuery<LandEventPageModel>({
    queryKey: ['land', 'event'],
    queryFn: ({ pageParam }) => requestLandEvents({ pageNo: pageParam, pageSize: 20 }),
    getNextPageParam: ({ page: { pageNo, isLast } }) => (isLast ? undefined : pageNo + 1),
    select: ({ pages, pageParams }) => {
      const [firstPage, ...restPages] = pages;
      return {
        pages: [
          {
            ...firstPage,
            weekList: firstPage.weekList.filter(({ status }) => status !== 'UPCOMING'),
          },
          ...restPages,
        ],
        pageParams,
      };
    },
    ...options,
  });

export const useUpcomingLandEvent = (options?: UseLandEventsOptions) => {
  const { data } = useLandEvents({
    ...options,
    select: undefined,
  });
  const latest = data?.pages[0]?.weekList[0];
  return latest?.status === 'UPCOMING' ? latest : undefined;
};

export const useLatestLandEvent = () => {
  const { data } = useLandEvents();

  return data?.pages[0]?.weekList[0];
};
